'use client';

import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { RelayType } from '@diritto/common/content/constants';
import { ReplyOrder } from '@diritto/common/reply/constants';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { ellipsis } from '@diritto/common/utils/string';
import { EpisodeReplyTab } from 'app-components/reply/EpisodeReplyTab';
import { ContentReplyListView } from 'app-components/reply/list/ContentReplyList';
import { EpisodeReplyProvider } from 'contexts/EpisodeReplyProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import Head from 'next/head';
import { usePostRelayContentQuery } from 'queries/relay/usePostRelayContentQuery';
import React, { useEffect, useMemo } from 'react';
import { FullPageLoading } from 'ui-components/common/Loading';
import { ErrorView } from 'ui-components/error/ErrorView';
import { ContentBodyNoPaddingContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { EpisodeReplyLayout } from 'ui-components/layout/EpisodeReplyLayout';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';

interface ContentReplyPageProps {
  contentId: number;
  orderBy?: ReplyOrder;
  isInputFocus?: boolean;
  isApp?: boolean;
}

export const ContentReplyPage = ({ contentId, orderBy, isInputFocus, isApp }: ContentReplyPageProps) => {
  useInAppHeader({ screen: WebViewScreen.ContentReply, hideHeader: true });
  useInAppStatusBar(WebViewScreen.ContentReply);

  const { tracker } = useTrackingState();
  const { isFetched: isContentFetched, data: contentData } = usePostRelayContentQuery(contentId);

  const contentPublic = contentData?.contentPublic;
  const authorProfilePublic = contentData?.authorProfilePublic;
  const title = useMemo(
    () => `${ellipsis(`전체 댓글${contentPublic?.title ? ` - ${contentPublic?.title}` : ''}`, 30)} | 디리토`,
    [contentPublic?.title],
  );

  useEffect(() => {
    tracker.track(TrackingEventType.ViewContentReply, { contentId });
  }, [contentId, tracker]);

  if (isContentFetched && !contentPublic) {
    return (
      <MutableHeaderLayout isApp={isApp}>
        {contentData?.content.relayType === RelayType.Private ? (
          <ErrorView title="비공개된 작품이에요." description="이 작품은 더 이상 볼 수 없어요." />
        ) : (
          <ErrorView title="삭제된 작품이에요." description="이 작품은 더 이상 볼 수 없어요." />
        )}
      </MutableHeaderLayout>
    );
  }

  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <EpisodeReplyProvider
        contentId={contentId}
        authorProfile={authorProfilePublic}
        orderBy={orderBy}
        isInputFocus={isInputFocus}>
        <EpisodeReplyLayout content={contentPublic}>
          {!isContentFetched && <FullPageLoading />}
          <EpisodeReplyTab replyCount={contentPublic?.counts.replyCount ?? 0} />
          <ContentBodyNoPaddingContainer className="overflow-y-scroll">
            <ContentReplyListView />
          </ContentBodyNoPaddingContainer>
        </EpisodeReplyLayout>
      </EpisodeReplyProvider>
    </>
  );
};
